import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import GuideLink from '@/components/common/GuideLink';
import positionService from '@/shared/services/positionService';
import { formatDeadlineDay } from '@/shared/utils/deadline';
import { studentWindowFor } from '@/shared/utils/studentWindow';

const plural = (n) => `${n} position${n === 1 ? '' : 's'} open`;

/**
 * A student with no application yet: the one thing to do, with what is open
 * and when it closes. The count is released positions with a place open now,
 * as the positions page lists them. A failed read drops the count and keeps
 * the action, because Browse positions works without it. A student who saved
 * a draft is pointed back to it instead.
 */
function FirstTimeHero({ term, draft }) {
    const [count, setCount] = useState(null);
    const termCode = term?.code;

    useEffect(() => {
        if (!termCode) return undefined;
        let cancelled = false;
        positionService
            .listPositions({ program: 'la', term: termCode })
            .then((data) => {
                if (!cancelled) setCount((data.positions || []).length);
            })
            .catch(() => {
                if (!cancelled) setCount(null);
            });
        return () => {
            cancelled = true;
        };
    }, [termCode]);

    const { closeDate } = studentWindowFor(term, 'la');
    const facts = [
        count !== null && plural(count),
        closeDate && `apply by ${formatDeadlineDay(closeDate)}`,
    ].filter(Boolean);

    if (draft?.id) {
        return (
            <Card
                as="section"
                aria-labelledby="first-time-hero"
                className="mb-4"
            >
                <Card.Body className="p-4">
                    <h2 id="first-time-hero" className="h3 mb-2">
                        Continue your application
                    </h2>
                    {closeDate && (
                        <p className="lead mb-3">
                            Apply by {formatDeadlineDay(closeDate)}
                        </p>
                    )}
                    <p className="mb-3">
                        Your Learning Assistant application is saved as a draft.
                        Pick up where you left off and submit it when it is
                        ready.
                    </p>
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <Button
                            as={Link}
                            to={`/apply/${draft.id}/edit`}
                            size="lg"
                        >
                            Continue application
                        </Button>
                        <GuideLink audience="student" />
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card as="section" aria-labelledby="first-time-hero" className="mb-4">
            <Card.Body className="p-4">
                <h2 id="first-time-hero" className="h3 mb-2">
                    Become a Learning Assistant
                </h2>
                {facts.length > 0 && (
                    <p className="lead mb-3">{facts.join(', ')}</p>
                )}
                <p className="mb-3">
                    Learning Assistants support active learning in a course
                    section. Pick the sections that fit your schedule and apply
                    in a few minutes.
                </p>
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <Button as={Link} to="/positions" size="lg">
                        Browse positions
                    </Button>
                    <GuideLink audience="student" />
                </div>
            </Card.Body>
        </Card>
    );
}

FirstTimeHero.propTypes = {
    term: PropTypes.shape({ code: PropTypes.string }),
    draft: PropTypes.shape({ id: PropTypes.string }),
};

export default FirstTimeHero;
